import {
  addDefaultProps,
  addDisplayName,
  addEffect,
  addProps,
  addRef,
  flowMax,
} from 'ad-hok'
import { FC } from 'react'
import { createUseStyles } from 'react-jss'
import { Link } from 'react-router-dom'
import { UsersWithImages, UserWithImages } from 'shared/src/api/types'
import { branchIfEmpty, branchIfFailsPredicate } from 'ad-hok-utils'
import { typedAs } from 'shared/src/utils/typedAs'
import {
  addDataLoadingFromPromise,
  hasData,
} from 'shared/src/utils/dataLoading'
import { addApi } from 'shared/src/api'
import { range } from 'lodash'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { serifFontFamily } from '../../utils/styles'
import { ExploreCreator } from './ExploreCreator'
import rightPointer from 'assets/right-pointer.svg'
import backgroundLineImage from 'assets/featured-creators-background-line.png'

const MIN_ITEMS = 4

interface StyleProps {
  backgroundImageUrl: string
}

const useStyles = createUseStyles({
  exploreCategory: {
    marginBottom: '76px',
  },
  link: {
    textDecoration: 'none',
  },
  exploreHeader: {
    fontFamily: serifFontFamily,
    color: '#ffffff',
    fontSize: 20,
    fontWeight: 700,
    lineHeight: '27px',
    letterSpacing: '0.02em',
    marginLeft: 28,
    marginBottom: 27,
  },
  exploreList: {
    display: 'flex',
    flexDirection: 'column',
    overflowX: 'scroll',
    overflowY: 'hidden',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  exploreListItemsContainer: {
    display: 'flex',
    flexDirection: 'row',
    position: 'relative',
    alignSelf: 'start',
    backgroundRepeat: 'repeat-x',
    backgroundPosition: '40% 40%',
    backgroundClip: 'content-box',
    backgroundImage: ({ backgroundImageUrl }: StyleProps) =>
      `url(${backgroundImageUrl})`,
  },
  creator: {
    marginLeft: 28,
    marginRight: 6,
    '&:last-child': {
      paddingRight: 40,
    },
  },
  rightPointer: {
    marginLeft: 11,
  },
  backgroundLineBeginningCover: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    backgroundColor: '#101010',
    width: 70,
  },
  backgroundLineEndCover: {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    backgroundColor: '#101010',
    width: 82,
  },
})

interface ExploreCategoryOrFeaturedProps {
  name: string
  to: string
  users: UserWithImages[]
  renderName?: () => JSX.Element
  showPlaceholders?: boolean
  backgroundColor?: string
  backgroundImageUrl?: string
  textColor?: string
}

export const ExploreCategoryOrFeatured: FC<ExploreCategoryOrFeaturedProps> =
  flowMax(
    addDisplayName('ExploreCategoryOrFeatured'),
    branchIfEmpty('users'),
    addRef('listItemsContainerRef', typedAs<HTMLDivElement | null>(null)),
    addEffect(
      ({ listItemsContainerRef }) =>
        () => {
          const listItemsContainerElement = listItemsContainerRef.current!
          listItemsContainerElement.style.width = `${listItemsContainerElement.scrollWidth}px`
        },
      ['users']
    ),
    addProps(({ backgroundImageUrl }) => ({
      classes: useStyles({
        backgroundImageUrl: backgroundImageUrl ?? backgroundLineImage,
      }),
    })),
    ({
      name,
      to,
      users,
      listItemsContainerRef,
      renderName,
      showPlaceholders,
      backgroundColor,
      textColor,
      classes,
    }) => (
      <div className={classes.exploreCategory}>
        {renderName ? (
          renderName()
        ) : (
          <Link to={to} className={classes.link}>
            <h3 className={classes.exploreHeader}>
              {name}
              <span className={classes.rightPointer}>
                <img src={rightPointer} alt={name} />
              </span>
            </h3>
          </Link>
        )}
        <div className={classes.exploreList}>
          <div
            ref={listItemsContainerRef}
            className={classes.exploreListItemsContainer}
          >
            <div
              className={classes.backgroundLineBeginningCover}
              style={backgroundColor ? { backgroundColor } : {}}
            />
            <div
              className={classes.backgroundLineEndCover}
              style={backgroundColor ? { backgroundColor } : {}}
            />
            {users.map((user) => (
              <ExploreCreator
                creator={user}
                textColor={textColor}
                className={classes.creator}
                key={user.id}
              />
            ))}
            {showPlaceholders && users.length < MIN_ITEMS
              ? range(0, MIN_ITEMS - users.length).map((index) => (
                  <ExploreCreator
                    textColor={textColor}
                    className={classes.creator}
                    key={`placeholder-${index}`}
                  />
                ))
              : null}
          </div>
        </div>
      </div>
    )
  )

interface ExploreFeaturedProps {
  creators: UsersWithImages
  name?: string
  renderName?: () => JSX.Element
  showPlaceholders?: boolean
  backgroundColor?: string
  backgroundImageUrl?: string
  textColor?: string
}

export const ExploreFeatured: FC<ExploreFeaturedProps> = flowMax(
  addDisplayName('ExploreFeatured'),
  addTranslationHelpers,
  addDefaultProps(({ t }) => ({
    name: t('menu.featuredCreators'),
  })),
  ({
    creators,
    name,
    renderName,
    showPlaceholders,
    backgroundColor,
    backgroundImageUrl,
    textColor,
  }) => (
    <ExploreCategoryOrFeatured
      name={name}
      users={creators}
      to="/"
      renderName={renderName}
      showPlaceholders={showPlaceholders}
      backgroundColor={backgroundColor}
      backgroundImageUrl={backgroundImageUrl}
      textColor={textColor}
    />
  )
)

interface ExploreFeaturedSelfDataLoadingProps {
  name?: string
  renderName?: () => JSX.Element
  backgroundColor?: string
}

export const ExploreFeaturedSelfDataLoading: FC<ExploreFeaturedSelfDataLoadingProps> =
  flowMax(
    addDisplayName('ExploreFeaturedSelfDataLoading'),
    addApi,
    addDataLoadingFromPromise(
      'featuredCreators',
      ({ api }) => api.users.featured(),
      [],
      () => 'featuredCreators'
    ),
    branchIfFailsPredicate('featuredCreators', hasData),
    addProps(({ featuredCreators: { data: featuredCreators } }) => ({
      featuredCreators,
    })),
    ({ featuredCreators, name, renderName, backgroundColor }) => (
      <ExploreFeatured
        creators={featuredCreators}
        name={name}
        renderName={renderName}
        backgroundColor={backgroundColor}
      />
    )
  )
